<template>
  <main class="mobile-page register-screen">
    <div class="mobile-screen">
      <header class="mobile-topnav">
        <RouterLink class="mobile-icon-btn" to="/mobile" aria-label="返回首页">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round"><path d="M14.5 5.5 8 12l6.5 6.5" /></svg>
        </RouterLink>
        <span class="mobile-topnav-title">受邀注册</span>
        <RouterLink class="mobile-icon-btn" to="/mobile/download" aria-label="直接下载">↓</RouterLink>
      </header>

      <section class="register-head">
        <span class="mobile-eyebrow">Invited Registration · 受邀加入</span>
        <h1>创建你的<br />点指之间账号</h1>
        <p>填写手机号并完成验证，即可加入点指之间数字生态。</p>
      </section>

      <form class="register-card" @submit.prevent="submit">
        <section class="invite-section" aria-live="polite">
          <div class="invite-heading">
            <span>邀请信息</span>
            <span v-if="inviterLoading" class="lookup-status">查询中...</span>
            <span v-else-if="inviter" class="lookup-status valid">邀请码有效</span>
          </div>
          <div class="invite-input">
            <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 12v7a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1v-7M8 8l4-4 4 4M12 4v11" /></svg>
            <input
              :value="inviteCode"
              maxlength="32"
              placeholder="输入好友邀请码（选填）"
              aria-label="邀请码，选填"
              @input="inviteCode = $event.target.value.trim()"
              @blur="lookupInviter"
            />
          </div>
          <div v-if="inviter" class="inviter">
            <img v-if="inviter.avatar" :src="inviter.avatar" alt="邀请人头像" />
            <i v-else>{{ inviterName.slice(0, 1) }}</i>
            <span><strong>{{ inviterName }}</strong><small>邀请你一起加入点指之间</small></span>
          </div>
        </section>

        <label>
          <span>手机号</span>
          <input v-model="form.phone" inputmode="tel" maxlength="11" placeholder="请输入 11 位手机号" />
        </label>
        <label>
          <span>验证码</span>
          <span class="code-line">
            <input v-model="form.code" inputmode="numeric" maxlength="6" placeholder="6 位验证码" />
            <button type="button" :disabled="countdown > 0" @click="sendVerification">{{ countdown ? `${countdown}s` : '获取验证码' }}</button>
          </span>
        </label>
        <label>
          <span>设置密码</span>
          <input v-model="form.password" type="password" minlength="6" maxlength="18" placeholder="6-18 位密码" />
        </label>
        <label>
          <span>确认密码</span>
          <input v-model="form.confirmPassword" type="password" minlength="6" maxlength="18" placeholder="再次输入密码" />
        </label>
        <label class="agreement">
          <input v-model="agreed" type="checkbox" />
          <p>
            我已阅读并同意
            <button type="button" @click="openAgreement('USER_AGREEMENT')">《用户协议》</button>
            和
            <button type="button" @click="openAgreement('PRIVATE_POLICY')">《隐私政策》</button>
          </p>
        </label>
        <button class="mobile-btn mobile-btn-primary" type="submit" :disabled="submitting">{{ submitting ? '正在注册' : '注册并下载' }}</button>
        <RouterLink class="download-app-link" to="/mobile/download">
          去下载 APP
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12h14m-5-5 5 5-5 5" /></svg>
        </RouterLink>
      </form>

      <AgreementDrawer
        v-model:show="agreementVisible"
        :type="agreementType"
        :agreement="agreement"
        :loading="agreementLoading"
        :error="agreementError"
        @retry="loadAgreement"
      />
    </div>
  </main>
</template>

<script setup>
import { computed, onMounted, reactive, ref } from 'vue'
import { useRoute } from 'vue-router'
import { showToast } from 'vant'
import { getAgreement, getInviterInfo, register, sendCode } from '@/mobile-api/auth'
import AgreementDrawer from '@/components/mobile/AgreementDrawer.vue'

const route = useRoute()
const form = reactive({ phone: '', code: '', password: '', confirmPassword: '' })
const inviter = ref(null)
const inviterLoading = ref(false)
const agreed = ref(false)
const submitting = ref(false)
const countdown = ref(0)
const agreementVisible = ref(false)
const agreementType = ref('USER_AGREEMENT')
const agreement = ref(null)
const agreementLoading = ref(false)
const agreementError = ref(false)
const agreementCache = new Map()
let timer = null

const inviteCode = ref('')
const lookupCode = ref('')
const inviteKeys = ['inviteCode', 'invite_code', 'info']
const sourceKeys = ['channel', 'campaign', 'utm_source', 'utm_campaign']
const source = ref('platform_internal_share')
const inviterName = computed(() => inviter.value?.name || inviter.value?.nickname || '点指之间用户')

function collectParams() {
  const params = new URLSearchParams(window.location.search)
  const hashQuery = window.location.hash.split('?')[1]
  if (hashQuery) {
    new URLSearchParams(hashQuery).forEach((value, key) => {
      if (!params.has(key)) params.set(key, value)
    })
  }
  Object.entries(route.query).forEach(([key, value]) => {
    if (!params.has(key) && value != null) {
      params.set(key, Array.isArray(value) ? value[0] : value)
    }
  })
  return params
}

function firstParam(params, keys) {
  for (const key of keys) {
    const value = params.get(key)?.trim()
    if (value) return value
  }
  return ''
}

async function lookupInviter() {
  const code = inviteCode.value.trim()
  if (!code) {
    inviter.value = null
    lookupCode.value = ''
    return
  }
  if (code === lookupCode.value && inviter.value) return

  lookupCode.value = code
  inviter.value = null
  inviterLoading.value = true
  try {
    const result = await getInviterInfo(code)
    const data = result?.data
    inviter.value = data && Object.keys(data).length ? data : null
    if (!inviter.value) showToast('暂未查询到该邀请码，请确认后重试')
  } catch {
    inviter.value = null
    // 请求拦截器已展示后端具体错误，这里避免用固定文案覆盖。
  } finally {
    inviterLoading.value = false
  }
}

onMounted(() => {
  const params = collectParams()
  inviteCode.value = firstParam(params, inviteKeys)
  source.value = (firstParam(params, sourceKeys) || 'platform_internal_share').slice(0, 64)
  if (inviteCode.value) lookupInviter()
})

function validPhone() {
  return /^1[3-9]\d{9}$/.test(form.phone)
}

async function sendVerification() {
  if (!validPhone()) return showToast('请输入正确的手机号')
  if (countdown.value) return
  try {
    await sendCode(form.phone)
    countdown.value = 60
    timer = window.setInterval(() => {
      countdown.value -= 1
      if (!countdown.value) window.clearInterval(timer)
    }, 1000)
    showToast('验证码已发送')
  } catch (error) {
    showToast(error.message || '验证码发送失败')
  }
}

async function submit() {
  if (!validPhone()) return showToast('请输入正确的手机号')
  if (!/^\d{6}$/.test(form.code)) return showToast('请输入 6 位验证码')
  if (form.password.length < 6 || form.password.length > 18) return showToast('密码长度需为 6-18 位')
  if (form.password !== form.confirmPassword) return showToast('两次密码不一致')
  if (!agreed.value) return showToast('请先同意协议')
  submitting.value = true
  try {
    await register({
      phone: form.phone,
      code: form.code,
      password: form.password,
      inviteCode: inviteCode.value.trim() || undefined,
      registerSource: source.value
    })
    sessionStorage.setItem('dzzj_register_success', JSON.stringify({
      inviteCode: inviteCode.value.trim(),
      source: source.value,
      phone: form.phone,
      inviter: inviter.value
    }))
    window.location.href = '/mobile/register/success'
  } catch {
    // 请求层已按响应 msg 展示注册失败原因，这里保留用户已填写内容。
  } finally {
    submitting.value = false
  }
}

async function loadAgreement() {
  if (agreementCache.has(agreementType.value)) {
    agreement.value = agreementCache.get(agreementType.value)
    return
  }
  agreementLoading.value = true
  agreementError.value = false
  try {
    const response = await getAgreement(agreementType.value)
    agreement.value = response.data
    agreementCache.set(agreementType.value, response.data)
  } catch {
    agreementError.value = true
  } finally {
    agreementLoading.value = false
  }
}

function openAgreement(type) {
  agreementType.value = type
  agreement.value = agreementCache.get(type) || null
  agreementVisible.value = true
  loadAgreement()
}
</script>

<style scoped>
.register-screen { padding-bottom: calc(40px + env(safe-area-inset-bottom)); }
.download-app-link { min-height:44px; display:flex; align-items:center; justify-content:center; gap:7px; margin-top:10px; color:var(--mobile-cyan); font-size:13px; font-weight:800; text-decoration:none; }
.download-app-link:active { opacity:.75; }
.register-head { padding: 18px 2px 20px; }
.register-head h1 { margin:12px 0 9px; font-size:29px; line-height:1.24; font-weight:900; }
.register-head p { color:var(--mobile-muted); font-size:12px; line-height:1.7; }
.invite-section { padding-bottom:15px; border-bottom:1px solid var(--mobile-line); }
.invite-heading { display:flex; align-items:center; justify-content:space-between; margin-bottom:7px; }
.invite-heading > span:first-child { color:var(--mobile-quiet); font-size:11.5px; font-weight:700; }
.lookup-status { color:var(--mobile-quiet); font-size:11px; font-weight:600; }
.lookup-status.valid { color:var(--mobile-cyan); }
.invite-input { min-height:48px; display:flex; align-items:center; gap:10px; padding:0 14px; border:1px solid var(--mobile-line); border-radius:12px; color:var(--mobile-quiet); background:rgba(255,255,255,.035); }
.invite-input:focus-within { border-color:rgba(98,244,214,.55); box-shadow:0 0 0 3px rgba(98,244,214,.08); }
.invite-input svg { flex:0 0 19px; }
.invite-input input { min-width:0; flex:1; border:0; color:var(--mobile-ink); background:transparent; font-size:14px; outline:none; }
.invite-input input::placeholder { color:var(--mobile-faint); }
.inviter { display:flex; align-items:center; gap:11px; margin-top:17px; padding:11px 12px; border-left:2px solid var(--mobile-cyan); border-radius:4px 12px 12px 4px; background:rgba(98,244,214,.07); }
.inviter img, .inviter i { width:36px; height:36px; flex:0 0 36px; border-radius:50%; object-fit:cover; }
.inviter i { width:36px; height:36px; flex:0 0 36px; display:flex; align-items:center; justify-content:center; border-radius:50%; color:var(--mobile-cta-ink); font-size:14px; font-weight:900; font-style:normal; background:radial-gradient(circle at 30% 25%,#8df5de,#46d6b6); }
.inviter span { min-width:0; }
.inviter strong { display:block; font-size:13px; font-weight:800; }
.inviter small { display:block; margin-top:2px; color:var(--mobile-quiet); font-size:11px; }
.register-card { display:grid; gap:13px; padding:18px 16px; border:1px solid var(--mobile-line); border-radius:18px; background:linear-gradient(165deg,rgba(18,30,50,.62),rgba(9,18,33,.78)); backdrop-filter:blur(12px); box-shadow:0 8px 28px rgba(0,0,0,.28); }
label { display:grid; gap:6px; }
label > span { color:var(--mobile-quiet); font-size:11.5px; font-weight:700; }
input { width:100%; min-height:48px; padding:0 14px; border:1px solid var(--mobile-line); border-radius:12px; color:var(--mobile-ink); background:rgba(255,255,255,.035); outline:none; }
input:focus { border-color:rgba(98,244,214,.55); box-shadow:0 0 0 3px rgba(98,244,214,.08); }
.code-line { display:grid; grid-template-columns:1fr 96px; gap:8px; }
.code-line button { min-height:48px; border-left:1px solid var(--mobile-line); color:var(--mobile-cyan); font-size:12px; font-weight:800; background:transparent; }
.code-line button:disabled { color:var(--mobile-faint); }
.agreement { display:flex; align-items:flex-start; gap:8px; }
.agreement input { width:16px; min-height:16px; height:16px; margin-top:2px; accent-color:#46d6b6; }
.agreement > p { display:block; color:var(--mobile-muted); font-size:12px; line-height:18px; }
.agreement button { color:var(--mobile-cyan); font-size:inherit; background:transparent; }
</style>
